import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { employeeService } from '@smartgrow/shared';

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;
const fmtDate = (s?: string) => s ? new Date(s).toLocaleDateString('en-NG') : '—';

const Row = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'right', maxWidth: '60%' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

export const EmployeeDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const employeeId = id!;

  const { data: employee, isLoading, isError } = useQuery({
    queryKey: ['employee', employeeId],
    queryFn: () => employeeService.getEmployeeById(employeeId),
    enabled: !!employeeId,
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  if (isError || !employee) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load employee.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{employee.fullName}</Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">{employee.employeeNumber}</Typography>
        </Box>
        <Chip label={employee.isActive ? 'Active' : 'Inactive'} color={employee.isActive ? 'success' : 'error'} size="small" />
        <Button startIcon={<Edit />} variant="outlined" size="small" onClick={() => navigate(`/dashboard/employees/${employeeId}/edit`)}>
          Edit
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Personal Info</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Full Name" value={employee.fullName} />
              <Row label="Position" value={employee.position} />
              <Row label="Email" value={employee.email} />
              <Row label="Phone" value={employee.phone} />
              <Row label="Hire Date" value={fmtDate(employee.hireDate)} />
              {employee.notes && <Row label="Notes" value={employee.notes} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Payment Details</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Monthly Salary" value={fmt(employee.monthlySalary)} />
              <Row label="Payment Day" value={`Day ${employee.paymentDay} of month`} />
              <Row label="Payment Method" value={employee.paymentMethod.replace('_', ' ')} />
              <Row label="Payment Enabled" value={employee.paymentEnabled ? 'Yes' : 'No'} />
              <Row label="Bank Name" value={employee.bankName} />
              <Row label="Account Number" value={employee.bankAccountNumber} />
            </CardContent>
          </Card>
        </Grid>

        {employee.username && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>System Access</Typography>
                <Divider sx={{ mb: 1 }} />
                <Row label="Username" value={employee.username} />
                <Row label="Role" value={employee.userRole} />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
